export const MY_ANALYSIS_NODE_ID = 'my-analysis';
export const ALL_BOARDS_NODE_ID = 'all-boards';

export type AdhocFolderSource = 'my-analysis' | 'tenant' | 'quattr' | 'personal';

export type AdhocFolderContent = {
  id?: string | number;
  deleted?: boolean;
  title?: string;
  folder_id?: string | number | null;
  isEmbedUserLook?: boolean;
  isEmbedUserDashboard?: boolean;
  last_accessed_at?: string | null;
  last_viewed_at?: string | null;
  created_at?: string | null;
  view_count?: number | string | null;
  content_metadata_id?: string | number;
};

export type AdhocFolderItem = {
  id?: string | number;
  name?: string;
  title?: string;
  parent_id?: string | number | null;
  source?: AdhocFolderSource;
  looks?: AdhocFolderContent[];
  dashboards?: AdhocFolderContent[];
  [key: string]: unknown;
};

export type AdhocFolderNode = {
  id: string;
  name: string;
  source: AdhocFolderSource;
  parentId: string | null;
  children: AdhocFolderNode[];
  looks: NonNullable<AdhocFolderItem['looks']>;
  dashboards: NonNullable<AdhocFolderItem['dashboards']>;
};

export type AdhocFolderSection = {
  id: 'personal' | 'tenant' | 'quattr';
  label: string;
  nodes: AdhocFolderNode[];
};

const asId = (value?: string | number | null): string | null => {
  if (value == null || value === '') {
    return null;
  }
  return String(value);
};

const folderName = (folder: AdhocFolderItem): string => String(folder.name || folder.title || 'Untitled folder');

const toNode = (folder: AdhocFolderItem, source: AdhocFolderSource): AdhocFolderNode => ({
  id: asId(folder.id) || folderName(folder),
  name: folderName(folder),
  source,
  parentId: asId(folder.parent_id),
  children: [],
  looks: Array.isArray(folder.looks) ? folder.looks : [],
  dashboards: Array.isArray(folder.dashboards) ? folder.dashboards : [],
});

/** Nest folders by parent_id. Sibling order is the Looker input order. */
export const nestFoldersByParent = (folders: AdhocFolderItem[] = [], source: AdhocFolderSource): AdhocFolderNode[] => {
  if (!folders.length) {
    return [];
  }

  const nodes = folders.map((folder) => toNode(folder, source));
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const childIds = new Set<string>();

  nodes.forEach((node) => {
    if (node.parentId && byId.has(node.parentId) && node.parentId !== node.id) {
      byId.get(node.parentId)!.children.push(node);
      childIds.add(node.id);
    }
  });

  return nodes.filter((node) => !childIds.has(node.id));
};

export const myAnalysisNode = (personal?: {
  looks?: AdhocFolderItem['looks'];
  dashboards?: AdhocFolderItem['dashboards'];
  children?: AdhocFolderNode[];
}): AdhocFolderNode => ({
  id: MY_ANALYSIS_NODE_ID,
  name: 'My Analysis',
  source: 'my-analysis',
  parentId: null,
  children: personal?.children ?? [],
  looks: personal?.looks ?? [],
  dashboards: personal?.dashboards ?? [],
});

const personalSubfolderIds = (folders: AdhocFolderItem[] = [], personalRootId?: string | null): Set<string> => {
  const ids = new Set<string>();
  if (!personalRootId) {
    return ids;
  }
  folders.forEach((folder) => {
    const id = asId(folder.id);
    if (id && id !== personalRootId) {
      ids.add(id);
    }
  });
  return ids;
};

const rootPersonalContent = (
  looks: AdhocFolderItem['looks'] = [],
  dashboards: AdhocFolderItem['dashboards'] = [],
  personalRootId?: string | null,
  subfolderIds?: Set<string>
) => {
  const activeLooks = (looks || []).filter((item) => !item?.deleted);
  const activeDashboards = (dashboards || []).filter((item) => !item?.deleted);

  if (!personalRootId || !subfolderIds?.size) {
    return { looks: activeLooks, dashboards: activeDashboards };
  }

  const inSubfolder = (item: AdhocFolderContent) => {
    const folderId = asId(item.folder_id);
    if (!folderId) {
      return Boolean(item.isEmbedUserLook || item.isEmbedUserDashboard);
    }
    if (folderId === personalRootId) {
      return true;
    }
    if (subfolderIds.has(folderId)) {
      return false;
    }
    return Boolean(item.isEmbedUserLook || item.isEmbedUserDashboard);
  };

  return {
    looks: activeLooks.filter((item) => inSubfolder(item)),
    dashboards: activeDashboards.filter((item) => inSubfolder(item)),
  };
};

export const buildAdhocFolderSections = (
  tenantFolders: AdhocFolderItem[] = [],
  quattrFolders: AdhocFolderItem[] = [],
  personal?: {
    looks?: AdhocFolderItem['looks'];
    dashboards?: AdhocFolderItem['dashboards'];
    personalFolders?: AdhocFolderItem[];
    personalRootId?: string | null;
  }
): AdhocFolderSection[] => {
  const personalRootId = personal?.personalRootId ? String(personal.personalRootId) : null;
  const personalFolderItems = (personal?.personalFolders || []).map((folder) => ({
    ...folder,
    source: 'personal' as const,
  }));
  const subfolderIds = personalSubfolderIds(personalFolderItems, personalRootId);
  const personalChildren = nestFoldersByParent(
    personalFolderItems.filter((folder) => asId(folder.id) !== personalRootId),
    'personal'
  );
  const rootContent = rootPersonalContent(personal?.looks, personal?.dashboards, personalRootId, subfolderIds);

  const tenantNodes = nestFoldersByParent(tenantFolders, 'tenant');
  const sections: AdhocFolderSection[] = [
    {
      id: 'personal',
      label: 'Personal',
      nodes: [
        myAnalysisNode({
          looks: rootContent.looks,
          dashboards: rootContent.dashboards,
          children: personalChildren,
        }),
      ],
    },
    // No "This tenant" heading — the Looker folder name is the row.
    { id: 'tenant', label: '', nodes: tenantNodes },
    { id: 'quattr', label: 'Quattr', nodes: nestFoldersByParent(quattrFolders, 'quattr') },
  ];

  return sections.filter((section) => section.nodes.length > 0);
};

export type WritableDestinationRow = {
  id: string;
  name: string;
  depth: number;
  disabled: boolean;
  group: 'Personal' | 'Shared';
};

/** Writable folder destinations for move/copy pickers (tenant + personal trees). */
export const writableDestinationNodes = (
  sections: AdhocFolderSection[],
  personalFolderId?: string | null,
  excludeId?: string | null
): WritableDestinationRow[] => {
  const destinations: WritableDestinationRow[] = [];
  const excluded = excludeId ? String(excludeId) : null;
  sections.forEach((section) => {
    const group: WritableDestinationRow['group'] = section.id === 'personal' ? 'Personal' : 'Shared';
    walkFolderNodes(section.nodes, (node, depth) => {
      if (node.source === 'quattr') {
        return;
      }
      if (node.id === MY_ANALYSIS_NODE_ID) {
        if (personalFolderId) {
          const id = String(personalFolderId);
          destinations.push({
            id,
            name: node.name,
            depth,
            disabled: false,
            group,
          });
        }
        return;
      }
      if (node.source === 'tenant' || node.source === 'personal' || node.source === 'my-analysis') {
        const isSharedRoot = section.id === 'tenant' && depth === 0;
        destinations.push({
          id: node.id,
          name: node.name,
          depth,
          disabled: Boolean(excluded && node.id === excluded && !isSharedRoot),
          group,
        });
      }
    });
  });
  return destinations;
};

export const folderDepthInSections = (sections: AdhocFolderSection[], id?: string | null): number | null => {
  if (!id) {
    return null;
  }
  let found: number | null = null;
  sections.forEach((section) => {
    walkFolderNodes(section.nodes, (node, depth) => {
      if (found == null && node.id === String(id)) {
        found = depth;
      }
    });
  });
  return found;
};

export const walkFolderNodes = (
  nodes: AdhocFolderNode[],
  visit: (node: AdhocFolderNode, depth: number) => void,
  depth = 0
): void => {
  nodes.forEach((node) => {
    visit(node, depth);
    if (node.children?.length) {
      walkFolderNodes(node.children, visit, depth + 1);
    }
  });
};

export const foldersMatchingName = (sections: AdhocFolderSection[], query?: string): AdhocFolderNode[] => {
  const needle = String(query || '')
    .trim()
    .toLowerCase();
  if (!needle) {
    return [];
  }
  const matched: AdhocFolderNode[] = [];
  sections.forEach((section) => {
    walkFolderNodes(section.nodes, (node) => {
      if (node.name.toLowerCase().includes(needle)) {
        matched.push(node);
      }
    });
  });
  return matched;
};

export const findFolderNode = (sections: AdhocFolderSection[], id?: string | null): AdhocFolderNode | null => {
  if (!id) {
    return null;
  }
  if (String(id) === ALL_BOARDS_NODE_ID) {
    return allBoardsNode();
  }
  let found: AdhocFolderNode | null = null;
  sections.forEach((section) => {
    walkFolderNodes(section.nodes, (node) => {
      if (!found && node.id === String(id)) {
        found = node;
      }
    });
  });
  return found;
};

/** Resolve a folder id from the tree, or synthesize a node from flat Looker folder records. */
export const findFolderNodeById = (
  sections: AdhocFolderSection[],
  id?: string | null,
  options?: {
    flatFolders?: AdhocFolderItem[];
    personalFolderId?: string | null;
    inferSource?: (folder: AdhocFolderItem) => AdhocFolderSource;
  }
): AdhocFolderNode | null => {
  const fromTree = findFolderNode(sections, id);
  if (fromTree) {
    return fromTree;
  }
  if (!id) {
    return null;
  }
  if (options?.personalFolderId && String(id) === String(options.personalFolderId)) {
    return findFolderNode(sections, MY_ANALYSIS_NODE_ID);
  }

  const flat = (options?.flatFolders || []).find((folder) => String(folder.id) === String(id));
  if (!flat) {
    return null;
  }

  const source: AdhocFolderSource =
    flat.source ||
    options?.inferSource?.(flat) ||
    (options?.personalFolderId && String(flat.parent_id) === String(options.personalFolderId) ? 'personal' : 'tenant');

  return toNode(flat, source);
};

export const allBoardsNode = (): AdhocFolderNode => ({
  id: ALL_BOARDS_NODE_ID,
  name: 'All Boards',
  source: 'my-analysis',
  parentId: null,
  children: [],
  looks: [],
  dashboards: [],
});

export const folderBreadcrumbs = (sections: AdhocFolderSection[], id?: string | null): AdhocFolderNode[] => {
  if (!id || String(id) === ALL_BOARDS_NODE_ID) {
    return [allBoardsNode()];
  }
  const trail: AdhocFolderNode[] = [];
  const walk = (nodes: AdhocFolderNode[], acc: AdhocFolderNode[]): boolean =>
    nodes.some((node) => {
      const next = [...acc, node];
      if (node.id === String(id)) {
        trail.push(...next);
        return true;
      }
      return walk(node.children, next);
    });

  sections.some((section) => walk(section.nodes, []));
  return [allBoardsNode(), ...trail];
};

/** Depth-first folder list in Looker order — for the All Boards landing groups. */
export const folderNodesInLookerOrder = (sections: AdhocFolderSection[]): AdhocFolderNode[] => {
  const nodes: AdhocFolderNode[] = [];
  sections.forEach((section) => {
    walkFolderNodes(section.nodes, (node) => {
      if (node.id !== ALL_BOARDS_NODE_ID) {
        nodes.push(node);
      }
    });
  });
  return nodes;
};

type CountableCard = { id?: string | number; title?: string; deleted?: boolean };

export const collectionHasId = (
  collection: Set<string | number> | Map<number, unknown> | undefined,
  id?: string | number | null
): boolean => {
  if (!collection || id == null || id === '') {
    return false;
  }
  if (collection.has(id as never)) {
    return true;
  }
  const numeric = Number(id);
  if (Number.isFinite(numeric) && collection.has(numeric as never)) {
    return true;
  }
  return collection.has(String(id) as never);
};

export const cardMatchesFolderSearch = (
  item: { title?: string; deleted?: boolean },
  folderName: string,
  query?: string
): boolean => {
  if (item?.deleted) {
    return false;
  }
  const needle = String(query || '')
    .trim()
    .toLowerCase();
  if (!needle) {
    return true;
  }
  if (
    String(folderName || '')
      .toLowerCase()
      .includes(needle)
  ) {
    return true;
  }
  return String(item.title || '')
    .toLowerCase()
    .includes(needle);
};

export const folderOwnCardCount = (
  node: AdhocFolderNode,
  options?: {
    filterFavorites?: boolean;
    favoriteLookIds?: Set<string | number> | Map<number, unknown>;
    favoriteDashboardIds?: Set<string | number> | Map<number, unknown>;
    query?: string;
    kind?: 'all' | 'look' | 'dashboard';
  }
): number => {
  const kind = options?.kind || 'all';

  const matches = (item: CountableCard, favoriteIds?: Set<string | number> | Map<number, unknown>) => {
    if (!cardMatchesFolderSearch(item, node.name, options?.query)) {
      return false;
    }
    if (options?.filterFavorites) {
      return collectionHasId(favoriteIds, item.id);
    }
    return true;
  };

  const looks = kind === 'dashboard' ? 0 : node.looks.filter((item) => matches(item, options?.favoriteLookIds)).length;
  const dashboards =
    kind === 'look' ? 0 : node.dashboards.filter((item) => matches(item, options?.favoriteDashboardIds)).length;
  return looks + dashboards;
};

export const folderSubtreeCardCount = (
  node: AdhocFolderNode,
  options?: Parameters<typeof folderOwnCardCount>[1]
): number =>
  folderOwnCardCount(node, options) +
  node.children.reduce((sum, child) => sum + folderSubtreeCardCount(child, options), 0);

const cardTitleMatches = (item: { title?: string; deleted?: boolean }, query: string): boolean =>
  !item?.deleted &&
  String(item.title || '')
    .toLowerCase()
    .includes(query);

const nodeOrDescendantMatches = (node: AdhocFolderNode, query: string): boolean => {
  if (node.name.toLowerCase().includes(query)) {
    return true;
  }
  if (
    node.looks.some((item) => cardTitleMatches(item, query)) ||
    node.dashboards.some((item) => cardTitleMatches(item, query))
  ) {
    return true;
  }
  return node.children.some((child) => nodeOrDescendantMatches(child, query));
};

const filterNode = (node: AdhocFolderNode, query: string): AdhocFolderNode | null => {
  if (!query) {
    return node;
  }
  if (!nodeOrDescendantMatches(node, query)) {
    return null;
  }
  return {
    ...node,
    children: node.children.map((child) => filterNode(child, query)).filter(Boolean) as AdhocFolderNode[],
  };
};

export const filterFolderSections = (sections: AdhocFolderSection[], query?: string): AdhocFolderSection[] => {
  const normalized = String(query || '')
    .trim()
    .toLowerCase();
  if (!normalized) {
    return sections;
  }

  return sections
    .map((section) => ({
      ...section,
      nodes: section.nodes.map((node) => filterNode(node, normalized)).filter(Boolean) as AdhocFolderNode[],
    }))
    .filter((section) => section.nodes.length > 0);
};

export const flattenVisibleFolderNodes = (
  sections: AdhocFolderSection[],
  expandedIds: Set<string>
): Array<{ node: AdhocFolderNode; depth: number }> => {
  const rows: Array<{ node: AdhocFolderNode; depth: number }> = [];
  const walk = (nodes: AdhocFolderNode[], depth: number) => {
    nodes.forEach((node) => {
      rows.push({ node, depth });
      if (node.children.length && expandedIds.has(node.id)) {
        walk(node.children, depth + 1);
      }
    });
  };
  sections.forEach((section) => walk(section.nodes, 0));
  return rows;
};

/** When the selected folder has no children, expose section roots so narrow screens can still reach tenant/Quattr. */
export const narrowFolderDestinations = (
  sections: AdhocFolderSection[],
  selectedId?: string | null
): AdhocFolderNode[] => {
  const selected = findFolderNode(sections, selectedId);
  if (selected?.children.length) {
    return selected.children;
  }
  return sections.flatMap((section) => section.nodes).filter((node) => node.id !== selected?.id);
};

export const adjacentVisibleFolderId = (
  rows: Array<{ node: AdhocFolderNode }>,
  selectedId: string,
  delta: number
): string | null => {
  if (!rows.length) {
    return null;
  }
  const index = rows.findIndex((row) => row.node.id === selectedId);
  if (index < 0) {
    return rows[0].node.id;
  }
  const next = rows[index + delta];
  return next ? next.node.id : rows[index].node.id;
};
